/**
 * Aligns each mega menu's first column with the top-level link that opens it,
 * so "By type" sits under "Lighting" rather than at the page margin.
 *
 * The offset differs per menu item and depends on the logo width, the utility
 * row and the rendered font, so it cannot be expressed in CSS alone.
 *
 * Alignment is measured from the GLYPHS, not the boxes. The top-level title is
 * a centred block wider than its own text: measured at 1440 the box starts at
 * 217 and the letters at 228, an 11px inset, while the submenu link's letters
 * start 1px inside its box. Aligning the boxes therefore left the text visibly
 * 10px out of line, and every box-based measurement read a perfect zero while
 * the rendering was wrong. A Range over the text node gives the real ink edge. This
 * script only writes a custom property. It never touches classes, attributes
 * or pointer events, so it stays clear of the open/close logic in
 * header-menu.js. The panel is laid out even while closed, so offsets are
 * measured up front rather than on hover.
 *
 * "When it fits" is measured, not guessed: the offset is applied only if the
 * remaining width still gives every column room for its widest link. Below
 * 990px the CSS ignores the property entirely.
 *
 * Recalculation matters more than the first run. The menu moves whenever the
 * brand face swaps in, the overflow list moves items into "More", the currency
 * selector changes width, or a merchant edits the menu in the theme editor. A
 * single pass on load measured the fallback font and wrongly rejected the
 * offset at wide viewports, so every one of those is now observed.
 */
(() => {
  const MIN_VIEWPORT = 990;
  const PROP = '--mega-menu-align-offset';
  const applied = new WeakMap();

  /**
   * The left edge of an element's rendered text, which is not its box edge when
   * the text is centred inside a wider box. Falls back to the box if the range
   * measures nothing, which happens for empty or replaced content.
   */
  const textLeft = (el) => {
    if (!el) return NaN;
    const range = document.createRange();
    range.selectNodeContents(el);
    const rect = range.getBoundingClientRect();
    range.detach?.();
    return rect && rect.width ? rect.left : el.getBoundingClientRect().left;
  };

  /**
   * Where the top-level label's text will sit once it has finished going bold.
   *
   * The label animates from weight 400 to 700 over 180ms and is centred in a
   * box wider than itself, so its left edge drifts left as it thickens.
   * Measuring it live means measuring a moving target: doing that twice, once
   * on open and once after the transition, made the panel visibly step
   * sideways as it appeared.
   *
   * So the settled width is measured off-document at the final weight and the
   * centred position derived from it. One pass, no waiting, nothing to see.
   */
  const settledTextLeft = (el) => {
    if (!el) return NaN;
    const box = el.getBoundingClientRect();
    const cs = getComputedStyle(el);
    const weight = cs.getPropertyValue('--menu-top-level-font-weight').trim() || '700';

    const probe = document.createElement('span');
    probe.textContent = el.textContent;
    probe.setAttribute(
      'style',
      'position:absolute;left:-9999px;top:0;visibility:hidden;white-space:pre;' +
        `font-family:${cs.fontFamily};font-size:${cs.fontSize};font-style:${cs.fontStyle};` +
        `font-weight:${weight === 'normal' ? 700 : Math.max(700, parseFloat(weight) || 700)};` +
        `letter-spacing:${cs.letterSpacing};text-transform:${cs.textTransform};` +
        `word-spacing:${cs.wordSpacing};font-stretch:${cs.fontStretch};`
    );
    document.body.appendChild(probe);
    const width = probe.getBoundingClientRect().width;
    probe.remove();

    if (!width || width > box.width) return textLeft(el);

    const align = cs.textAlign;
    if (align === 'center') return box.left + (box.width - width) / 2;
    if (align === 'right' || align === 'end') return box.right - width;
    return box.left;
  };

  const align = () => {
    document.querySelectorAll('.header .menu-list__list-item').forEach((item) => {
      const link = item.querySelector('.menu-list__link');
      const grid = item.querySelector('.mega-menu__grid');
      if (!link || !grid) return;

      const write = (value) => {
        // Writing only on change keeps the ResizeObserver below from
        // retriggering itself.
        if (applied.get(grid) === value) return;
        applied.set(grid, value);
        if (value) grid.style.setProperty(PROP, value);
        else grid.style.removeProperty(PROP);
      };

      if (window.innerWidth < MIN_VIEWPORT) {
        write(null);
        return;
      }

      // Measure against the unshifted grid, otherwise the offset compounds.
      const previous = applied.get(grid);
      if (previous) grid.style.removeProperty(PROP);

      const gridRect = grid.getBoundingClientRect();
      const firstLink = grid.querySelector('.mega-menu__link');
      const title = link.querySelector('.menu-list__link-title') || link;
      const offset = Math.round(settledTextLeft(title) - textLeft(firstLink));
      const styles = getComputedStyle(grid);
      const columns = styles.gridTemplateColumns.split(' ').filter(Boolean).length;
      const gap = parseFloat(styles.columnGap) || 0;
      const links = [...grid.querySelectorAll('.mega-menu__link')];

      if (previous) grid.style.setProperty(PROP, previous);
      if (!Number.isFinite(offset) || offset <= 0 || !gridRect.width || !columns || !links.length) {
        write(null);
        return;
      }

      const widest = Math.max(...links.map((el) => Math.ceil(el.scrollWidth)));
      const available = gridRect.width - offset - gap * (columns - 1);
      write(available / columns < widest ? null : `${offset}px`);
    });
  };

  let frame;
  const schedule = () => {
    cancelAnimationFrame(frame);
    frame = requestAnimationFrame(align);
  };

  const observe = () => {
    const header = document.querySelector('.header');
    const list = document.querySelector('.header .menu-list');
    if (!header) return;

    // Catches the font swap, the overflow list moving items into "More", and
    // the currency selector changing width. None of these fire a resize event.
    if (window.ResizeObserver) {
      const ro = new ResizeObserver(schedule);
      ro.observe(header);
      if (list) ro.observe(list);
    }

    // Catches menu edits in the theme editor, which replace the markup.
    if (list && window.MutationObserver) {
      new MutationObserver(schedule).observe(list, { childList: true, subtree: true });
    }
  };

  schedule();
  observe();
  addEventListener('resize', schedule);
  addEventListener('load', schedule);
  addEventListener('shopify:section:load', schedule);
  addEventListener('shopify:section:reorder', schedule);
  addEventListener('shopify:block:select', schedule);
  if (document.fonts?.ready) document.fonts.ready.then(schedule);

  /**
   * header-menu.js hydrates the menu after this runs and can replace or
   * rewrite the nodes we wrote to, which silently dropped the property on
   * first load: the value was measured correctly and applied, then lost, and
   * only a later resize brought it back. The ladder covers that window.
   */
  [0, 150, 400, 900, 1800, 3000].forEach((delay) => setTimeout(schedule, delay));

  /**
   * The ladder alone is not enough, because it stops. Anything that moves the
   * menu afterwards leaves a stale offset: a logo that decodes late, a font
   * that swaps on a slow connection, a cached older script. That showed up as
   * an 11px misalignment on a real machine while every measurement here read
   * exactly 0.
   *
   * So recompute when the panel is about to be shown. These listeners are
   * passive and read-only, and only ever write a custom property, so they
   * cannot interfere with the open and close logic they sit alongside.
   * pointerenter fires before the panel finishes its transition, and write()
   * is deduped, so a value that is already correct costs nothing.
   */
  const nav = document.querySelector('.header .menu-list') || document.querySelector('.header');
  if (nav) {
    nav.addEventListener('pointerover', schedule, { passive: true });
    nav.addEventListener('focusin', schedule, { passive: true });
  }

  // A logo that decodes after the ladder has finished shifts every item right.
  document.querySelectorAll('.header img').forEach((img) => {
    if (!img.complete) img.addEventListener('load', schedule, { once: true, passive: true });
  });
})();
